/**
 * Design Tokens — PDI IaC Agent
 * Palette inspired by PDI MCP Directory: clean whites, navy darks, PDI blue accents.
 * Dark mode uses deep navy (#1a1f2e) instead of pure black for a professional look.
 */

export const tokens = {
  // Color tokens
  colors: {
    // Primary — PDI Blue (matches MCP Directory #1a73e8 family)
    primary: {
      50:  '#e8f0fe',
      100: '#c5d8fc',
      200: '#9dbef9',
      300: '#74a4f6',
      400: '#4d8ef3',
      500: '#1a73e8', // main — PDI brand blue
      600: '#1565d8',
      700: '#1055c0',
      800: '#0b44a8',
      900: '#062f80',
    },
    // Secondary — PDI Teal accent
    secondary: {
      50:  '#e0f7f4',
      100: '#b2ece4',
      200: '#80dfd3',
      300: '#4dd2c1',
      400: '#26c7b3',
      500: '#00bca6', // main
      600: '#00a994',
      700: '#00937e',
      800: '#007d69',
      900: '#005a4a',
    },
    // Semantic colors
    success: {
      50:  '#e6f4ea',
      100: '#ceead6',
      200: '#a8d5b5',
      300: '#81c995',  // softer green
      400: '#57bb75',
      500: '#34a853',  // Google-style green — readable
      600: '#2d9248',
      700: '#267a3c',
      800: '#1e6230',
      900: '#144a22',
    },
    error: {
      50:  '#fce8e6',
      100: '#f9c6c2',
      200: '#f5a09a',
      300: '#f07a72',
      400: '#ea5a50',
      500: '#ea4335',  // Google-style red
      600: '#d93025',
      700: '#c5221f',
      800: '#b31412',
      900: '#960000',
    },
    warning: {
      50:  '#fef7e0',
      100: '#fdedb3',
      200: '#fce282',
      300: '#fbd754',
      400: '#fbce2f',
      500: '#fbbc04',  // Google-style amber
      600: '#f9ab00',
      700: '#f29900',
      800: '#ea8600',
      900: '#e37400',
    },
    info: {
      50:  '#e8f4fd',
      100: '#c5e3fb',
      200: '#9fd0f8',
      300: '#74bcf5',
      400: '#4facf2',
      500: '#1a9be8',
      600: '#1589d4',
      700: '#1076be',
      800: '#0b62a6',
      900: '#064880',
    },
    // Neutral — cool grey (not warm grey, matches PDI Directory)
    grey: {
      50:  '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    // Background colors (light mode) — slightly tinted, not pure white
    background: {
      default:  '#f0f4f8',  // cool blue-grey tint — gives depth vs pure white paper
      paper:    '#ffffff',
      elevated: '#ffffff',
    },
    // Text colors (light mode)
    text: {
      primary:   'rgba(15, 23, 42, 0.92)',   // slate-900 near-black
      secondary: 'rgba(71, 85, 105, 0.85)',  // slate-600
      disabled:  'rgba(148, 163, 184, 0.7)', // slate-400
      contrast:  '#ffffff',
    },
    // Dark mode — deep navy, NOT pure black
    dark: {
      background: {
        default:  '#0f172a',  // slate-900 navy
        paper:    '#1e293b',  // slate-800
        elevated: '#293548',  // slightly lighter card
      },
      text: {
        primary:   'rgba(241, 245, 249, 0.95)', // slate-100
        secondary: 'rgba(148, 163, 184, 0.85)', // slate-400
        disabled:  'rgba(100, 116, 139, 0.6)',  // slate-500
        contrast:  '#0f172a',
      },
    },
  },

  // Spacing scale (8px base unit)
  spacing: {
    xs:   4,
    sm:   8,
    md:   16,
    lg:   24,
    xl:   32,
    xxl:  48,
    xxxl: 64,
  },

  // Typography — Inter/DM Sans feel (system stack)
  typography: {
    fontFamily: {
      primary:   '"Inter", "DM Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      monospace: '"JetBrains Mono", "Fira Code", "Roboto Mono", "Courier New", monospace',
    },
    fontSize: {
      xs:   '0.75rem',   // 12px
      sm:   '0.875rem',  // 14px
      base: '1rem',      // 16px
      md:   '1.125rem',  // 18px
      lg:   '1.25rem',   // 20px
      xl:   '1.5rem',    // 24px
      xxl:  '2rem',      // 32px
      xxxl: '2.5rem',    // 40px
    },
    fontWeight: {
      light:    300,
      regular:  400,
      medium:   500,
      semibold: 600,
      bold:     700,
    },
    lineHeight: {
      tight:   1.2,
      normal:  1.5,
      relaxed: 1.75,
      loose:   2,
    },
  },

  // Shadows — subtle, not heavy
  shadows: {
    xs:  '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
    sm:  '0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px -1px rgba(15, 23, 42, 0.06)',
    md:  '0 4px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.05)',
    lg:  '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.04)',
    xl:  '0 20px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)',
    xxl: '0 25px 50px -12px rgba(15, 23, 42, 0.18)',
  },

  // Border radius
  borderRadius: {
    xs:   2,
    sm:   4,
    md:   8,
    lg:   12,
    xl:   16,
    xxl:  24,
    full: 9999,
  },

  // Transitions
  transitions: {
    fast:   '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow:   '500ms cubic-bezier(0.4, 0, 0.2, 1)',
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut:   'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn:    'cubic-bezier(0.4, 0, 1, 1)',
      sharp:     'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },

  // Touch target sizes (WCAG AA)
  touchTarget: {
    minimum:     48,
    comfortable: 56,
    large:       64,
  },

  // Breakpoints (mobile-first)
  breakpoints: {
    xs:  0,
    sm:  600,
    md:  960,
    lg:  1280,
    xl:  1920,
  },

  // Z-index layers
  zIndex: {
    base:     0,
    dropdown: 1000,
    sticky:   1100,
    fixed:    1200,
    modal:    1300,
    popover:  1400,
    tooltip:  1500,
  },

  // Accessibility tokens
  accessibility: {
    contrastRatio: {
      minimum:  4.5,
      large:    3,
      enhanced: 7,
    },
    focusRing: {
      width:  2,
      offset: 2,
      color:  '#1a73e8',
      style:  'solid',
    },
  },
} as const;

// Type exports
export type Tokens = typeof tokens;
export type ColorTokens = typeof tokens.colors;
export type SpacingTokens = typeof tokens.spacing;
export type TypographyTokens = typeof tokens.typography;
